import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetUserInvoiceQuery,
  useAdminPayInvoiceMutation,
  useGetLedgerAccountsQuery,
} from "../../api/billingApi";
import {
  Loader2,
  ChevronLeft,
  ShieldCheck,
  Hash,
  ArrowRight,
  Wallet,
  RefreshCw,
  User,
  Mail,
  X,
  Upload,
  Banknote,
  AlertCircle,
} from "lucide-react";

/** Industrial Money Formatter */
function money(n: number) {
  return `₦${(n / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function AdminUserInvoicePage() {
  const { userId, invoiceId } = useParams<{
    userId: string;
    invoiceId: string;
  }>();
  const navigate = useNavigate();

  // Modal & Edit State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [payAmount, setPayAmount] = useState<string>("");
  const [isEditingAmount, setIsEditingAmount] = useState(false);

  // Form State
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [bankAccountId, setBankAccountId] = useState<string>("");
  const [paymentDate, setPaymentDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );

  // Error Handling State
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useGetUserInvoiceQuery(
    { userId: userId!, invoiceId: invoiceId! },
    { skip: !userId || !invoiceId },
  );

  const [uploadPayment, { isLoading: isUploading }] =
    useAdminPayInvoiceMutation();

  const { data: ledgerAccounts = [] } = useGetLedgerAccountsQuery();

  const invoice = data?.invoice;
  const user = data?.user;

  const enteredKobo = Math.round(parseFloat(payAmount || "0") * 100);
  const advanceKobo = invoice ? Math.max(0, enteredKobo - invoice.balance) : 0;

  // Update input when invoice balance changes
  useEffect(() => {
    if (invoice && !isEditingAmount) {
      setPayAmount((invoice.balance / 100).toString());
    }
  }, [invoice?.balance, isEditingAmount]);

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Frontend Validations
    if (!bankAccountId) {
      setFormError("Please select the bank account the payment was made into.");
      return;
    }
    if (!paymentDate) {
      setFormError("Payment date is required.");
      return;
    }
    const maxSize = 2048 * 1024; // 2MB
    if (proofFile && proofFile.size > maxSize) {
      setFormError("The file is too large. Maximum size is 2MB.");
      return;
    }

    const formData = new FormData();
    formData.append("amount", payAmount);
    formData.append("payment_method", paymentMethod);
    if (proofFile) formData.append("proof_of_payment", proofFile);
    formData.append("reference", reference);
    formData.append("notes", notes);
    formData.append("bank_account_id", bankAccountId);
    formData.append("paid_at", paymentDate);

    try {
      const result = await uploadPayment({
        invoiceId: invoiceId!,
        formData,
      }).unwrap();

      navigate(`/receipt/${result.receipt_number || result.reference}`);
    } catch (err: any) {
      console.error("Payment Error:", err);

      // Extract backend validation or logic errors
      if (err?.status === 422 && err?.data?.errors) {
        const validationErrors = err.data.errors;
        const firstKey = Object.keys(validationErrors)[0];
        setFormError(validationErrors[firstKey][0]);
      } else if (err?.status === 409) {
        setFormError(err?.data?.message || "This transaction reference has already been used.");
      } else {
        setFormError(
          err?.data?.message || "Protocol Error: Unable to sync with ledger.",
        );
      }
    }
  };

  if (isLoading || !invoice) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex flex-col items-center justify-center p-6">
        <RefreshCw className="w-8 h-8 text-orange-500 animate-spin mb-4" />
        <span className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-400">
          Syncing Protocol...
        </span>
      </div>
    );
  }

  const isPaid = invoice.status === "paid";

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-20 text-slate-900 font-sans">
      <div className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-all"
          >
            <ChevronLeft size={14} /> Return to Registry
          </button>
        </div>

        {/* IDENTITY CARD */}
        <div className="bg-white border border-slate-100 rounded-[2rem] p-6 mb-8 shadow-sm flex items-center gap-6">
          <div className="w-16 h-16 rounded-[1.5rem] bg-slate-900 flex items-center justify-center text-white shadow-lg shadow-slate-200">
            <User size={28} strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-[1000] text-slate-900 uppercase tracking-tight leading-none">
              {user?.name}
            </h2>
            <div className="flex flex-wrap gap-4 mt-2">
              <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400">
                <Mail size={13} /> {user?.email}
              </span>
              <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider">
                <Hash size={13} /> {user?.membership_no || "PROVISIONAL"}
              </span>
            </div>
          </div>
        </div>

        {/* MAIN TERMINAL */}
        <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-xl shadow-slate-200/40 overflow-hidden">
          <div className="h-2 bg-slate-900 w-full" />
          <div className="p-8 md:p-10 border-b border-slate-50 bg-slate-50/20">
            <div className="flex flex-col md:flex-row justify-between items-start gap-6">
              <div className="space-y-3">
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest ${
                    isPaid
                      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                      : "bg-rose-50 text-rose-700 border-rose-100"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      isPaid ? "bg-emerald-500" : "bg-rose-500 animate-pulse"
                    }`}
                  />
                  {isPaid ? "Settled" : "Pending Action"}
                </div>
                <h1 className="text-3xl font-[1000] text-slate-900 tracking-tight uppercase">
                  {invoice.invoice_number}
                  <span className="text-orange-500">.</span>
                </h1>
              </div>
            </div>
          </div>

          <div className="p-8 md:p-10">
            <div className="space-y-4">
              {invoice.items.map((item: any) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center py-4 border-b border-slate-50 hover:bg-slate-50/50 px-2 rounded-lg transition-all"
                >
                  <div className="text-sm font-[900] uppercase text-slate-800">
                    {item.description} {item?.year ? `(${item?.year})` : ""}
                  </div>
                  <div className="text-sm font-black text-slate-900">
                    {money(item.amount)}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
              <div
                className={`p-6 rounded-2xl border transition-all ${
                  isEditingAmount
                    ? "bg-white border-slate-900 shadow-lg"
                    : "bg-slate-50 border-transparent"
                }`}
              >
                <div className="flex justify-between items-center mb-4">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <Wallet size={12} /> Entry Amount
                  </label>
                  <button
                    onClick={() => setIsEditingAmount(!isEditingAmount)}
                    className="text-[9px] font-black text-orange-600 uppercase hover:underline"
                  >
                    {isEditingAmount ? "Lock Entry" : "Modify Amount"}
                  </button>
                </div>
                <div className="flex items-center text-3xl font-[1000] text-slate-900">
                  <span className="text-slate-300 mr-2">₦</span>
                  <input
                    type="number"
                    disabled={!isEditingAmount}
                    className="bg-transparent outline-none w-full disabled:text-slate-500"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2 text-right">
                <div className="text-[10px] font-black text-slate-400 uppercase">
                  Current Balance Due
                </div>
                <div className="text-4xl font-[1000] text-slate-900 tracking-tighter">
                  {money(invoice.balance)}
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 bg-slate-900 flex justify-between items-center">
            <div className="flex items-center gap-4 text-white">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                <ShieldCheck size={20} className="text-orange-500" />
              </div>
              <div className="text-[9px] font-black uppercase tracking-[0.2em]">
                System Manual Override <br />
                <span className="text-slate-500">
                  Syncing to financial ledger
                </span>
              </div>
            </div>

            {invoice.balance > 0 ? (
              <button
                onClick={() => {
                  setFormError(null);
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-3 bg-orange-500 text-white px-8 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-white hover:text-slate-900 transition-all shadow-xl shadow-orange-500/20 active:scale-95"
              >
                Upload Manual Payment <ArrowRight size={14} />
              </button>
            ) : (
              <div className="text-emerald-400 text-[10px] font-black uppercase flex items-center gap-2 border border-emerald-500/20 px-4 py-2 rounded-lg bg-emerald-500/5">
                <ShieldCheck size={14} /> Protocol Fully Settled
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --- PAYMENT MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-lg rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            <div className="p-6 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-900 flex items-center gap-2">
                <Banknote size={18} className="text-orange-500" /> Record
                Offline Payment
              </h3>
              <button
                disabled={isUploading}
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-slate-200 rounded-full transition-colors disabled:opacity-30"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleManualSubmit} className="p-8 space-y-6 overflow-y-auto flex-1">
              {/* ADVANCE PREVIEW */}
              {advanceKobo > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-2xl flex items-start gap-3">
                  <Wallet size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-[10px] font-black text-emerald-700 uppercase tracking-widest mb-0.5">
                      Advance Credit
                    </h4>
                    <p className="text-xs font-bold text-emerald-700 leading-tight">
                      Amount exceeds invoice balance.{" "}
                      <span className="font-black">{money(advanceKobo)}</span>{" "}
                      will be credited to this member's advance account.
                    </p>
                  </div>
                </div>
              )}

              {/* INLINE ERROR ALERT */}
              {formError && (
                <div className="bg-rose-50 border border-rose-100 p-4 rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                  <AlertCircle size={18} className="text-rose-500 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-[10px] font-black text-rose-700 uppercase tracking-widest mb-1">
                      Execution Error
                    </h4>
                    <p className="text-xs font-bold text-rose-600 leading-tight">
                      {formError}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase mb-2 block tracking-widest">
                  Payment Channel
                </label>
                <select
                  className="w-full bg-slate-50 border border-slate-100 p-4 rounded-xl font-bold text-sm outline-none focus:border-orange-500 transition-all appearance-none"
                  value={paymentMethod}
                  onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    setFormError(null);
                  }}
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash Payment</option>
                  <option value="pos">POS Terminal</option>
                </select>
              </div>

              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase mb-2 block tracking-widest">
                  Bank Account Paid Into
                </label>
                <select
                  required
                  className={`w-full bg-slate-50 border p-4 rounded-xl font-bold text-sm outline-none focus:border-orange-500 transition-all appearance-none ${
                    formError && !bankAccountId
                      ? "border-rose-300"
                      : "border-slate-100"
                  }`}
                  value={bankAccountId}
                  onChange={(e) => {
                    setBankAccountId(e.target.value);
                    setFormError(null);
                  }}
                >
                  <option value="">— Select account —</option>
                  {ledgerAccounts.map((acc: any) => (
                    <option key={acc.id} value={acc.id}>
                      {[
                        acc.account_name ?? acc.name,
                        acc.code,
                        acc.account_number,
                      ]
                        .filter(Boolean)
                        .join(" — ")}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase mb-2 block tracking-widest">
                  Date Payment Was Made
                </label>
                <input
                  type="date"
                  required
                  max={new Date().toISOString().split("T")[0]}
                  className="w-full bg-slate-50 border border-slate-100 p-4 rounded-xl font-bold text-sm outline-none focus:border-orange-500 transition-all"
                  value={paymentDate}
                  onChange={(e) => {
                    setPaymentDate(e.target.value);
                    setFormError(null);
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[9px] font-black text-slate-400 uppercase mb-2 block tracking-widest">
                    Transaction Reference
                  </label>
                  <input
                    type="text"
                    placeholder="TX-123..."
                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-xl font-bold text-sm outline-none focus:border-slate-900"
                    value={reference}
                    onChange={(e) => {
                      setReference(e.target.value);
                      setFormError(null);
                    }}
                    required
                  />
                </div>
                <div>
                  <label className="text-[9px] font-black text-slate-400 uppercase mb-2 block tracking-widest">
                    Amount (₦)
                  </label>
                  <div className="p-4 bg-slate-100 rounded-xl font-[1000] text-slate-900 leading-none flex items-center h-[54px]">
                    {payAmount}
                  </div>
                </div>
              </div>

              <div className="relative group">
                <label className="text-[9px] font-black text-slate-400 uppercase mb-2 block text-center tracking-widest">
                  Proof of Payment (PDF/Image){" "}
                  <span className="normal-case font-bold text-slate-300">— Optional</span>
                </label>
                <div
                  className={`border-2 border-dashed rounded-3xl p-8 flex flex-col items-center justify-center transition-all ${
                    proofFile
                      ? "border-emerald-500 bg-emerald-50/30"
                      : "border-slate-200 bg-slate-50 group-hover:border-orange-500"
                  }`}
                >
                  <Upload
                    size={32}
                    className={
                      proofFile ? "text-emerald-500" : "text-slate-300"
                    }
                  />
                  <span className="text-xs font-bold mt-2 text-slate-600 truncate max-w-[200px]">
                    {proofFile ? proofFile.name : "Select or Drop File"}
                  </span>
                  <input
                    type="file"
                    onChange={(e) => {
                      setProofFile(e.target.files?.[0] || null);
                      setFormError(null);
                    }}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    accept=".jpg,.jpeg,.png,.pdf"
                  />
                </div>
              </div>

              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase mb-2 block tracking-widest">
                  Administrative Notes
                </label>
                <textarea
                  rows={2}
                  className="w-full bg-slate-50 border border-slate-100 p-4 rounded-xl font-bold text-sm outline-none focus:border-slate-900"
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    setFormError(null);
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={isUploading}
                className="w-full bg-slate-900 text-white p-5 rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-orange-500 transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {isUploading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <>
                    Finalize & Sync Ledger <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
